<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  open: { type: Boolean, default: false },
  contact: { type: Object, default: () => ({}) },
  submitting: { type: Boolean, default: false },
  success: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();
const date = ref('');
const dateInput = ref('');
const nativeDatePicker = ref(null);
const today = ref('');
const time = ref('09:00');
const nativeTimePicker = ref(null);
const type = ref('reminder');
const title = ref('');
const notes = ref('');
const dateError = ref('');
const titleError = ref('');
const clientName = computed(
  () => props.contact?.name || t('INBOX.FOLLOWUPS.MODAL.CONVERSATION_CLIENT')
);
const clientPhone = computed(
  () => props.contact?.phone_number || props.contact?.phone || ''
);

const localDate = value => {
  const dateValue = value || new Date();
  return [
    dateValue.getFullYear(),
    String(dateValue.getMonth() + 1).padStart(2, '0'),
    String(dateValue.getDate()).padStart(2, '0'),
  ].join('-');
};
const formatDate = value => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};
const currentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};
const normalizeDateInput = value => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
const handleDateInput = event => {
  dateInput.value = normalizeDateInput(event.target.value);
  const match = dateInput.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) {
    date.value = '';
    dateError.value = '';
    return;
  }
  const [, day, month, year] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() !== Number(month) - 1 ||
    parsed.getDate() !== Number(day)
  ) {
    date.value = '';
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.INVALID_DATE');
    return;
  }
  date.value = localDate(parsed);
  dateError.value =
    date.value < localDate() ? t('INBOX.FOLLOWUPS.MODAL.DATE_NOT_PAST') : '';
};
const showNativePicker = picker => {
  const input = picker.value;
  if (!input) return;
  if (typeof input.showPicker === 'function') {
    input.showPicker();
    return;
  }
  input.focus();
  input.click();
};
const openDatePicker = () => showNativePicker(nativeDatePicker);
const openTimePicker = () => showNativePicker(nativeTimePicker);
const handleNativeDateChange = event => {
  if (!event.target.value) return;
  date.value = event.target.value;
  dateInput.value = formatDate(date.value);
  dateError.value = date.value < localDate() ? t('INBOX.FOLLOWUPS.MODAL.DATE_NOT_PAST') : '';
};
const handleNativeTimeChange = event => {
  if (!event.target.value) return;
  time.value = event.target.value;
  dateError.value =
    date.value === localDate() && time.value < currentTime()
      ? t('INBOX.FOLLOWUPS.MODAL.FUTURE_TIME')
      : '';
};
const handleTimeInput = event => {
  const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
  time.value =
    digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
  if (digits.length < 4) {
    dateError.value = '';
    return;
  }
  const hour = Number(digits.slice(0, 2));
  const minute = Number(digits.slice(2));
  if (hour > 23 || minute > 59) {
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.TIME_RANGE');
    return;
  }
  dateError.value =
    date.value === localDate() && time.value < currentTime()
      ? t('INBOX.FOLLOWUPS.MODAL.FUTURE_TIME')
      : '';
};
const submitFollowup = () => {
  if (props.submitting) return;

  const timeMatch = time.value.match(/^(\d{2}):(\d{2})$/);
  if (!date.value) {
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.INVALID_DATE');
    return;
  }
  if (!timeMatch) {
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.INVALID_TIME');
    return;
  }
  if (Number(timeMatch[1]) > 23 || Number(timeMatch[2]) > 59) {
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.TIME_RANGE');
    return;
  }
  if (dateError.value) return;
  if (date.value === localDate() && time.value < currentTime()) {
    dateError.value = t('INBOX.FOLLOWUPS.MODAL.FUTURE_TIME');
    return;
  }
  if (!title.value.trim()) {
    titleError.value = t('INBOX.FOLLOWUPS.MODAL.TITLE_REQUIRED');
    return;
  }

  titleError.value = '';
  emit('submit', {
    scheduled_date: date.value,
    scheduled_time: time.value,
    type: type.value,
    title: title.value.trim(),
    notes: notes.value.trim(),
  });
};

watch(
  () => props.open,
  value => {
    if (!value) return;
    today.value = localDate();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    date.value = localDate(tomorrow);
    dateInput.value = formatDate(date.value);
    time.value = '09:00';
    type.value = 'reminder';
    title.value = '';
    titleError.value = '';
    notes.value = '';
    dateError.value = '';
  }
);
</script>

<template>
  <div
    v-show="open"
    class="followup-overlay"
    @click.self="emit('close')"
  >
    <form
      v-if="!success"
      class="followup-modal"
      @submit.prevent="submitFollowup"
    >
      <header class="followup-modal__head">
        <div class="followup-modal__heading">
          <span class="i-lucide-calendar-check size-5" aria-hidden="true" />
          <h2 class="followup-modal__title">
            {{ t('INBOX.FOLLOWUPS.MODAL.CREATE_TITLE') }}
          </h2>
        </div>
        <button
          type="button"
          class="followup-modal__close"
          :aria-label="t('INBOX.FOLLOWUPS.MODAL.CLOSE')"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" aria-hidden="true" />
        </button>
      </header>
      <div class="followup-modal__body">
        <p class="followup-modal__intro">
          {{ t('INBOX.FOLLOWUPS.MODAL.DESCRIPTION') }}
        </p>
        <div class="followup-label followup-label--schedule">
          {{ t('INBOX.FOLLOWUPS.MODAL.DATE_AND_TIME') }}
          <span class="followup-required" aria-hidden="true" />
        </div>
        <div class="followup-date-grid">
          <label class="followup-date-field">
            {{ t('INBOX.FOLLOWUPS.MODAL.DATE') }}
            <div class="followup-date-input">
              <input
                ref="nativeDatePicker"
                class="followup-native-picker"
                type="date"
                :value="date"
                :min="today"
                tabindex="-1"
                aria-hidden="true"
                @change="handleNativeDateChange"
              />
              <button
                type="button"
                :aria-label="t('INBOX.FOLLOWUPS.MODAL.OPEN_CALENDAR')"
                @click.stop="openDatePicker"
              >
                <span class="i-lucide-calendar-days" aria-hidden="true" />
              </button>
              <input
                :value="dateInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                required
                :placeholder="t('INBOX.FOLLOWUPS.MODAL.DATE_PLACEHOLDER')"
                maxlength="10"
                @input="handleDateInput"
              />
            </div>
          </label>
          <label class="followup-date-field">
            {{ t('INBOX.FOLLOWUPS.MODAL.TIME') }}
            <div class="followup-date-input">
              <input
                ref="nativeTimePicker"
                class="followup-native-picker"
                type="time"
                :value="time"
                tabindex="-1"
                aria-hidden="true"
                @change="handleNativeTimeChange"
              />
              <button
                type="button"
                :aria-label="t('INBOX.FOLLOWUPS.MODAL.OPEN_TIME_PICKER')"
                @click.stop="openTimePicker"
              >
                <span class="i-lucide-clock-3" aria-hidden="true" />
              </button>
              <input
                :value="time"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                required
                :placeholder="t('INBOX.FOLLOWUPS.MODAL.TIME_PLACEHOLDER')"
                maxlength="5"
                @input="handleTimeInput"
              />
            </div>
          </label>
        </div>
        <div class="followup-label followup-label--type">
          {{ t('INBOX.FOLLOWUPS.MODAL.TYPE_LABEL') }}
        </div>
        <label class="followup-radio">
          <input v-model="type" type="radio" value="reminder" />
          <span class="followup-radio__text">
            <strong>{{ t('INBOX.FOLLOWUPS.MODAL.SIMPLE_REMINDER') }}</strong>
            <small>{{
              t('INBOX.FOLLOWUPS.MODAL.SIMPLE_REMINDER_DESCRIPTION')
            }}</small>
          </span>
        </label>
        <label class="followup-radio">
          <input v-model="type" type="radio" value="task" />
          <span class="followup-radio__text">
            <strong>{{ t('INBOX.FOLLOWUPS.MODAL.TASK') }}</strong>
            <small>{{ t('INBOX.FOLLOWUPS.MODAL.TASK_DESCRIPTION') }}</small>
          </span>
        </label>
        <label class="followup-field">
          <span>
            {{ t('INBOX.FOLLOWUPS.MODAL.TITLE') }}
            <b class="followup-required" aria-hidden="true" />
          </span>
          <input
            v-model="title"
            maxlength="255"
            required
            :placeholder="t('INBOX.FOLLOWUPS.MODAL.TITLE_PLACEHOLDER')"
            @input="titleError = ''"
          />
          <small v-if="titleError" class="followup-error" role="alert">{{
            titleError
          }}</small>
        </label>
        <label class="followup-field">
          <span class="followup-field__caption">
            <span>{{ t('INBOX.FOLLOWUPS.MODAL.NOTES') }}</span>
            <small>{{ t('INBOX.FOLLOWUPS.MODAL.OPTIONAL') }}</small>
          </span>
          <textarea
            v-model="notes"
            rows="3"
            maxlength="5000"
            :placeholder="t('INBOX.FOLLOWUPS.MODAL.NOTES_PLACEHOLDER')"
          />
        </label>
        <div class="followup-linked">
          <span class="i-lucide-link-2" aria-hidden="true" />
          <span>
            <strong>{{ t('INBOX.FOLLOWUPS.MODAL.LINK_TITLE') }}</strong>
            <small>{{
              t('INBOX.FOLLOWUPS.MODAL.LINK_DESCRIPTION', {
                client: clientName,
                phone: clientPhone ? ` · ${clientPhone}` : '',
              })
            }}</small>
          </span>
        </div>
        <p v-if="dateError" class="followup-error" role="alert">
          {{ dateError }}
        </p>
      </div>
      <footer class="followup-modal__foot">
        <button type="button" class="followup-button" @click="emit('close')">
          {{ t('INBOX.FOLLOWUPS.MODAL.CANCEL') }}
        </button>
        <button
          type="submit"
          class="followup-button followup-button--primary"
          :disabled="submitting"
        >
          {{
            submitting
              ? t('INBOX.FOLLOWUPS.MODAL.CREATING')
              : t('INBOX.FOLLOWUPS.MODAL.CREATE')
          }}
        </button>
      </footer>
    </form>
    <div v-else class="followup-modal followup-modal--success">
      <div class="followup-success__icon">
        <span class="i-lucide-check" aria-hidden="true" />
      </div>
      <h2 class="followup-success__title">
        {{ t('INBOX.FOLLOWUPS.MODAL.SUCCESS_TITLE') }}
      </h2>
      <p class="followup-success__text">
        {{ t('INBOX.FOLLOWUPS.MODAL.SUCCESS_DESCRIPTION') }}
      </p>
      <p class="followup-success__linked">
        <span class="i-lucide-message-circle-check" aria-hidden="true" />
        {{
          t('INBOX.FOLLOWUPS.MODAL.LINKED_TO_CLIENT', { client: clientName })
        }}
      </p>
      <button
        type="button"
        class="followup-button followup-button--primary"
        @click="emit('close')"
      >
        {{ t('INBOX.FOLLOWUPS.MODAL.OK') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.followup-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(0 0 0 / 45%);
}

.followup-modal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: min(520px, calc(100vw - 28px));
  max-height: 90vh;
  overflow: hidden;
  border-radius: 12px;
  background: var(--n-surface-1, #fff);
  color: var(--n-slate-12, #1f2937);
  box-shadow: 0 18px 50px rgb(0 0 0 / 25%);
}

.followup-modal__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px 10px;
}

.followup-modal__heading {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 8px;
  color: var(--n-slate-11, #52525b);
}

.followup-modal__title {
  flex: 1;
  margin: 0;
  color: var(--n-slate-12, #1f2937);
  font-size: 17px;
  font-weight: 700;
}

.followup-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--n-slate-10, #71717a);
  cursor: pointer;
}

.followup-modal__close:hover {
  background: var(--n-alpha-2, #f4f4f5);
}
.followup-modal__body {
  overflow-y: auto;
  padding: 0 24px 10px;
}
.followup-modal__intro {
  margin: 0 0 14px;
  color: var(--n-slate-11, #52525b);
  font-size: 13px;
  line-height: 1.45;
}
.followup-label {
  color: var(--n-slate-12, #1f2937);
  font-size: 13px;
  font-weight: 600;
}
.followup-required {
  color: #dc2626;
  font-weight: 700;
}
.followup-required::after {
  content: '*';
}
.followup-label--schedule {
  margin-bottom: 8px;
}
.followup-date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.followup-date-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: var(--n-slate-11, #52525b);
  font-size: 12px;
  font-weight: 600;
}
.followup-date-input {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 40px;
  border: 1px solid var(--n-weak, #e5e7eb);
  border-radius: 8px;
  background: var(--n-surface-1, #fff);
}
.followup-date-input:focus-within {
  border-color: #22c55e;
  outline: 2px solid rgb(34 197 94 / 15%);
}
.followup-date-input > button {
  position: relative;
  z-index: 2;
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--n-slate-11, #52525b);
  cursor: pointer;
}
.followup-date-input > button > span {
  width: 16px;
  height: 16px;
}
.followup-date-input > input:not(.followup-native-picker) {
  box-sizing: border-box;
  width: 0;
  min-width: 0;
  flex: 1 1 auto;
  height: 40px;
  padding: 0 12px 0 0;
  border: 0;
  border-radius: 0 8px 8px 0;
  background: transparent;
  color: var(--n-slate-12, #1f2937);
  font: inherit;
  font-size: 13px;
  font-weight: 400;
}
.followup-field input:focus,
.followup-field textarea:focus {
  border-color: #22c55e;
  outline: 2px solid rgb(34 197 94 / 15%);
}
.followup-date-input > input.followup-native-picker {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
.followup-label--type {
  margin-top: 12px;
}
.followup-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
  color: var(--n-slate-12, #1f2937);
  font-size: 13px;
  cursor: pointer;
}
.followup-radio > input {
  margin-top: 3px;
  accent-color: #16a34a;
}
.followup-radio__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.followup-radio__text small,
.followup-field small {
  color: var(--n-slate-11, #52525b);
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
}
.followup-field .followup-error {
  color: #b91c1c;
}
.followup-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  color: var(--n-slate-12, #1f2937);
  font-size: 13px;
  font-weight: 600;
}
.followup-field__caption {
  display: flex;
  align-items: baseline;
  gap: 3px;
}
.followup-field input,
.followup-field textarea {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid var(--n-weak, #e5e7eb);
  border-radius: 8px;
  background: var(--n-surface-1, #fff);
  color: var(--n-slate-12, #1f2937);
  font: inherit;
  font-size: 13px;
  font-weight: 400;
}
.followup-field input {
  height: 40px;
  padding: 0 12px;
}
.followup-field textarea {
  height: 80px;
  min-height: 80px;
  padding: 10px 12px;
  resize: none;
}
.followup-linked {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f0fdf4;
  color: #16a34a;
  font-size: 13px;
}
.followup-linked > span:first-child {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
}
.followup-linked > span:last-child {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.followup-linked small {
  color: var(--n-slate-11, #52525b);
  font-size: 12px;
  line-height: 17px;
}
.followup-error {
  margin: 12px 0 0;
  color: #b91c1c;
  font-size: 13px;
}
.followup-modal__foot {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 24px 20px;
}
.followup-button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 13px;
  border: 1px solid var(--n-weak, #e5e7eb);
  border-radius: 7px;
  background: var(--n-surface-1, #fff);
  color: var(--n-slate-12, #1f2937);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s,
    transform 0.05s;
}
.followup-button:hover {
  background: var(--n-alpha-2, #f4f4f5);
}
.followup-button:active {
  transform: translateY(1px);
}
.followup-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.followup-button--primary {
  padding: 0 16px;
  border-color: #22c55e;
  background: #22c55e;
  color: #fff;
}
.followup-button--primary:hover {
  border-color: #16a34a;
  background: #16a34a;
}
.followup-modal--success {
  align-items: center;
  padding: 28px 24px;
  text-align: center;
}
.followup-success__icon {
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #dcfce7;
  color: #16a34a;
}
.followup-success__icon > span {
  width: 26px;
  height: 26px;
}
.followup-success__title {
  margin: 0;
  color: var(--n-slate-12, #1f2937);
  font-size: 18px;
  font-weight: 700;
}
.followup-success__text {
  margin: 9px 0 22px;
  color: var(--n-slate-11, #52525b);
  font-size: 13px;
  line-height: 1.45;
}
.followup-success__linked {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 20px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #dcfce7;
  color: #16a34a;
  text-align: left;
  font-size: 12px;
}
.followup-success__linked > span {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@media (max-width: 360px) {
  .followup-date-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
